import React, { useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";

const TWEEN_FACTOR_BASE = 0.35;

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max);

const EmblaCarousel = (props) => {
  const {
    slides,
    options,
    onSelect,
    activeIndex,
    isRecording,
    recordingFadeOut,
  } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const tweenFactor = useRef(0);
  const tweenNodes = useRef([]);

  const setTweenNodes = useCallback((emblaApi) => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector(".embla__slide__number");
    });
  }, []);

  const setTweenFactor = useCallback((emblaApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback((emblaApi, eventName) => {
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const slidesInView = emblaApi.slidesInView();
    const isScrollEvent = eventName === "scroll";

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress);
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const scale = numberWithinRange(tweenValue, 0, 1).toString();
        const tweenNode = tweenNodes.current[slideIndex];
        tweenNode.style.transform = `scale(${scale})`; //DDNT REMOVE
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenScale)
      .on("scroll", tweenScale)
      .on("slideFocus", tweenScale)
      .on("select", tweenScale)
      .on("select", () => {
        if (typeof onSelect === "function") {
          onSelect(emblaApi.selectedScrollSnap());
        }
      });
  }, [emblaApi, tweenScale, onSelect]);

  useEffect(() => {
    const handleHamburgerClick = () => {
      const hamburger = document.getElementById("hamburger");
      const mobileMenu = document.getElementById("mobile-menu");

      hamburger.classList.toggle("active");
      mobileMenu.classList.toggle("active");

      // Disable scrolling when menu is open
      if (mobileMenu.classList.contains("active")) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto"; // Restore scrolling
      }
    };

    const hamburger = document.getElementById("hamburger");
    if (hamburger) {
      hamburger.addEventListener("click", handleHamburgerClick);
    }

    return () => {
      if (hamburger) {
        hamburger.removeEventListener("click", handleHamburgerClick);
      }
    };
  }, []);

  const handleTap = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((lens, index) => (
            <div
              className="embla__slide"
              key={lens.id || index}
              onClick={() => handleTap(index)}
            >
              <img
                className={`embla__slide__number${
                  activeIndex === index && isRecording
                    ? " active-slide-recording" +
                      (recordingFadeOut ? " fade-out" : "")
                    : ""
                }`}
                src={lens.iconUrl}
                alt={lens.name || `Lens ${index}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="embla__controls"></div>
    </div>
  );
};

// To trigger fade-out, pass 'recordingFadeOut' prop as true when recording ends.

export default EmblaCarousel;
